import {
  CalendarCommandError,
  CalendarCommandSession,
  calendarReconcileColumns,
  type CalendarReconcileSubmitInput,
  type CalendarSingleView,
  type CalendarSourceCommandSession,
  createCalendarEventRange,
  parseCalendarAttendees,
  readCalendarEvent,
  resolveCalendarReconcileContext,
  toCalendarReconcileSnapshot,
  writeCalendarCellValue,
} from '@blocksuite/affine/data-view';
import { presentCanvasDatabaseView } from '@blocksuite/affine-block-database';
import { readDatabaseShardDirectoryDocIds } from '@streamlain/database-identity';
import { Service } from '@toeverything/infra';

import type { DefaultServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import {
  CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
  DatabaseViewLocalDiscoveryService,
  type LocalDatabaseView,
  viewBackedAppResourceId,
  viewBackedAppSourceRefKey,
  viewBackedAreaScope,
} from '../view-backed-app';
import { isCalendarSourceEventEligible } from './calendar-source-eligibility';
import type {
  CalendarEventColumnMap,
  CalendarEventsResponse,
  CalendarMode,
  CalendarProjectedEvent,
  CalendarSource,
  CalendarSourceCandidate,
  CalendarSourcesResponse,
} from './types';
import { toCalendarAreaRef } from './types';

const CALENDAR_SOURCE_COLORS = [
  '#2f80ed',
  '#27ae60',
  '#f2994a',
  '#eb5757',
  '#9b51e0',
  '#00a3a3',
] as const;

interface LocalCalendarView extends LocalDatabaseView {
  columns: CalendarEventColumnMap;
  view: CalendarSingleView | null;
}

interface LocalSourceProjection {
  events: CalendarProjectedEvent[];
  source: CalendarEventsResponse['sources'][number];
}

export interface CalendarLocalSourceInput {
  /** Limits the projection to these docs; every workspace doc when omitted. */
  docIds?: readonly string[];
  pendingAreaByDocId?: ReadonlyMap<string, string>;
  serverSources?: readonly CalendarSource[];
}

export interface CalendarLocalEventCreateInput {
  allDay?: boolean;
  day: Date;
  endMinute: number;
  startMinute: number;
}

export interface CalendarLocalEventCreateResult {
  reconcile: CalendarReconcileSubmitInput;
  rowId: string;
  startAt: string;
}

export interface CalendarLocalEventUpdateInput {
  allDay?: boolean;
  endAt: Date;
  rowId: string;
  startAt: Date;
}

interface CalendarLocalAccount {
  avatar?: string | null;
  id: string;
  info?: { avatarUrl?: string | null; name?: string | null } | null;
}

export function resolveLocalCalendarPresentation(input: {
  account: CalendarLocalAccount | null;
  area: CalendarSource['area'];
}): Pick<CalendarSource, 'owner' | 'presentationError'> {
  if (!input.area || input.area.kind !== 'private') {
    return { owner: null, presentationError: null };
  }
  const accountId = input.account?.id.trim();
  const ownerUserId = input.area.ownerUserId?.trim();
  const displayName = input.account?.info?.name?.trim();
  if (!accountId || !ownerUserId || accountId !== ownerUserId || !displayName) {
    return {
      owner: null,
      presentationError: 'owner_profile_unavailable',
    };
  }
  return {
    owner: {
      avatarUrl:
        input.account?.info?.avatarUrl?.trim() ||
        input.account?.avatar?.trim() ||
        null,
      displayName,
      userId: accountId,
    },
    presentationError: null,
  };
}

export class CalendarLocalProjectionService extends Service {
  constructor(
    private readonly docsService: DocsService,
    private readonly discovery: DatabaseViewLocalDiscoveryService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  async openCommandSession(
    source: CalendarSource,
    enqueueCreated: (input: CalendarReconcileSubmitInput) => Promise<void>
  ): Promise<CalendarSourceCommandSession> {
    if (
      !isCalendarSourceEventEligible(source) ||
      source.lifecycleState !== 'active'
    )
      throw new CalendarCommandError(
        'no-source',
        'calendar.chrome.chooseCalendar'
      );
    const opened = await this.discovery
      .openDocWithContent(source.docId)
      .catch(() => {
        throw new CalendarCommandError(
          'unavailable',
          'calendar.chrome.unavailable'
        );
      });
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view)
        throw new CalendarCommandError(
          'unavailable',
          'calendar.chrome.unavailable'
        );
      return new CalendarCommandSession({
        view: localView.view,
        canEdit: () => source.canEdit && source.lifecycleState === 'active',
        release: opened.release,
        onCreated: async ({ rowId }) => {
          const context = resolveCalendarReconcileContext(
            localView.view!.manager.dataSource
          );
          const columns = calendarReconcileColumns(localView.view!);
          if (!context || !columns) {
            localView.view!.rowsDelete([rowId]);
            throw new CalendarCommandError(
              'unavailable',
              'calendar.chrome.unavailable'
            );
          }
          await enqueueCreated({
            context,
            columns,
            rowId,
            previousEvent: null,
            skipMail: true,
            viewId: localView.view!.id,
          });
        },
        onRenamed: async previousEvent => {
          const context = resolveCalendarReconcileContext(
            localView.view!.manager.dataSource
          );
          const columns = calendarReconcileColumns(localView.view!);
          if (!context || !columns)
            throw new CalendarCommandError(
              'unavailable',
              'calendar.chrome.unavailable'
            );
          await enqueueCreated({
            context,
            columns,
            rowId: previousEvent.rowId,
            previousEvent: toCalendarReconcileSnapshot(
              previousEvent,
              context,
              localView.view!.id
            ),
            skipMail: true,
            viewId: localView.view!.id,
          });
        },
        observeChanges: onChange => {
          const observed = new Set<object>();
          const stops: Array<() => void> = [];
          const observe = (store: CalendarObservedStore) =>
            observeCalendarDocument(store, onChange, observed, stops);
          observe(opened.store);
          if (localView.dataSource?.storageModel) {
            observeCalendarRowDocs(
              localView.dataSource,
              observe,
              onChange,
              stops
            );
          }
          return () => stops.forEach(stop => stop());
        },
      });
    } catch (error) {
      opened.release();
      if (error instanceof CalendarCommandError) throw error;
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    }
  }

  async listLocalSources(
    input: CalendarLocalSourceInput = {}
  ): Promise<CalendarSourcesResponse> {
    const account = this.defaultServerService.server.account$.value;
    const serverSourcesByKey = new Map(
      (input.serverSources ?? []).map(source => [
        viewBackedAppSourceRefKey(source),
        source,
      ])
    );
    const sources: CalendarSource[] = [];
    const views = await this.discovery.listLocalViews({
      descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      docIds: input.docIds,
      pendingAreaByDocId: input.pendingAreaByDocId,
    });

    for (const rawView of views) {
      const view = this.toCalendarView(rawView);
      const key = viewBackedAppSourceRefKey({
        blockId: view.blockId,
        databaseId: view.databaseId,
        docId: view.docId,
        viewId: view.viewId,
      });
      const serverSource = serverSourcesByKey.get(key);
      if (serverSource) {
        const localProjectionError = view.projectionError ?? null;
        const projectionError =
          localProjectionError ??
          (serverSource.projectionError === 'area-unavailable' &&
          serverSource.area
            ? null
            : serverSource.projectionError);
        sources.push({
          ...serverSource,
          columns: view.columns,
          projectionError,
          syncState: serverSource.syncState ?? 'synced',
        });
        continue;
      }
      sources.push(
        this.toLocalSource({
          account,
          docId: view.docId,
          projectionError: view.area ? null : 'area-unavailable',
          view,
        })
      );
    }

    return {
      sources: sources.sort(compareSources),
    };
  }

  async listLocalCandidates(
    input: CalendarLocalSourceInput = {}
  ): Promise<{ candidates: CalendarSourceCandidate[] }> {
    const { sources } = await this.listLocalSources(input);
    return {
      candidates: sources.map(source => ({
        appKind: source.appKind,
        area: source.area,
        blockId: source.blockId,
        databaseId: source.databaseId,
        docId: source.docId,
        documentTitle:
          this.docsService.list.doc$(source.docId).value?.title$.value ?? null,
        projectionError: source.projectionError ?? null,
        title: source.title,
        viewId: source.viewId,
        viewMode: source.viewMode,
      })),
    };
  }

  /**
   * Projects every source concurrently. `onProgress` receives the sources
   * projected so far after each one settles, so one slow calendar document
   * (for example a database whose storage never becomes ready) does not hold
   * back the calendars that are already available locally.
   */
  async projectLocalEvents(
    input: {
      mode: CalendarMode;
      sources: readonly CalendarSource[];
      windowEndAt: string;
      windowStartAt: string;
    },
    onProgress?: (partial: CalendarEventsResponse) => void
  ): Promise<CalendarEventsResponse> {
    const start = parseWindowDate(input.windowStartAt, 'windowStartAt');
    const end = parseWindowDate(input.windowEndAt, 'windowEndAt');
    const settled: (LocalSourceProjection | undefined)[] = input.sources.map(
      () => undefined
    );
    const response = (): CalendarEventsResponse => {
      const projections = settled.filter(
        (projection): projection is LocalSourceProjection => !!projection
      );
      // Every calendar view of a database is a source of its own. A row that
      // several shown views of one database hold is one event: the first
      // source in the list shows it.
      const shownRows = new Set<string>();
      const events = settled.flatMap((projection, index) => {
        const source = input.sources[index]!;
        return (projection?.events ?? []).filter(event => {
          const row = `${source.docId}:${source.databaseId}:${event.rowId}`;
          if (shownRows.has(row)) return false;
          shownRows.add(row);
          return true;
        });
      });
      return {
        calendarEventContractVersion: 2,
        events: events.sort((a, b) => a.startAt.localeCompare(b.startAt)),
        sources: projections.map(projection => projection.source),
        window: {
          endAt: input.windowEndAt,
          mode: input.mode,
          startAt: input.windowStartAt,
        },
      };
    };

    await Promise.all(
      input.sources.map(async (source, index) => {
        settled[index] = await this.projectLocalSource(source, start, end);
        if (settled.some(projection => !projection)) {
          onProgress?.(response());
        }
      })
    );
    return response();
  }

  private async projectLocalSource(
    source: CalendarSource,
    start: Date,
    end: Date
  ): Promise<LocalSourceProjection> {
    const withoutEvents = (error: string | null): LocalSourceProjection => ({
      events: [],
      source: projectedSource(source, error),
    });
    if (!source.visible || source.lifecycleState !== 'active') {
      return withoutEvents(source.visible ? source.lifecycleState : 'hidden');
    }
    if (!isCalendarSourceEventEligible(source)) {
      return withoutEvents(source.projectionError ?? source.presentationError!);
    }

    const opened = await this.discovery
      .openDocWithContent(source.docId)
      .catch(error => {
        console.warn('[CalendarLocalProjection] Failed to open source doc', {
          docId: source.docId,
          error,
        });
        return null;
      });
    if (!opened) {
      return withoutEvents('doc-unavailable');
    }
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView) {
        return withoutEvents('view-unavailable');
      }
      if (!localView.view) {
        return withoutEvents(localView.projectionError ?? 'projection-failed');
      }
      // Events project from all rows; while directory parts of the database
      // are loading, the source shows loading instead of a part of them, and
      // a part the server does not hold makes the source unavailable.
      const directory = localView.dataSource?.getCompleteShardDirectory();
      if (directory?.status === 'loading') return withoutEvents('rows-loading');
      if (directory?.status === 'missing') return withoutEvents('rows-missing');
      const events = this.projectSourceEvents({
        end,
        source,
        start,
        view: localView.view,
      });
      return events
        ? { events, source: projectedSource(source, null) }
        : withoutEvents('pending');
    } catch (error) {
      console.warn(
        '[CalendarLocalProjection] Failed to project source events',
        {
          error,
          sourceId: source.id,
        }
      );
      return withoutEvents(
        error instanceof CalendarCommandError && error.code === 'unavailable'
          ? 'unavailable'
          : 'projection-failed'
      );
    } finally {
      opened.release();
    }
  }

  async createLocalEvent(
    source: CalendarSource,
    input: CalendarLocalEventCreateInput
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDocWithContent(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }

      const created = await createCalendarEventRange(
        localView.view,
        input.day,
        input.startMinute,
        input.endMinute,
        { allDay: input.allDay }
      );
      if (!created) throw new Error('Calendar event could not be created.');

      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        localView.view.rowsDelete([created.rowId]);
        throw new Error('Calendar reconcile context is unavailable.');
      }

      return {
        reconcile: {
          columns,
          context,
          previousEvent: null,
          rowId: created.rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId: created.rowId,
        startAt: created.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async updateLocalEvent(
    source: CalendarSource,
    input: CalendarLocalEventUpdateInput
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDocWithContent(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }
      const previousEvent = readCalendarEvent(localView.view, input.rowId, {
        includeDetails: true,
      });
      if (!previousEvent) {
        throw new Error('Calendar event is not available locally.');
      }
      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        throw new Error('Calendar reconcile context is unavailable.');
      }

      writeCalendarCellValue(
        localView.view,
        input.rowId,
        localView.view.startDateCol$.value,
        input.startAt.getTime()
      );
      writeCalendarCellValue(
        localView.view,
        input.rowId,
        localView.view.endDateCol$.value,
        input.endAt.getTime()
      );
      if (input.allDay !== undefined) {
        writeCalendarCellValue(
          localView.view,
          input.rowId,
          localView.view.allDayCol$.value,
          input.allDay
        );
      }

      return {
        reconcile: {
          columns,
          context,
          previousEvent: toCalendarReconcileSnapshot(
            previousEvent,
            context,
            localView.view.id
          ),
          rowId: input.rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId: input.rowId,
        startAt: input.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async deleteLocalEvent(
    source: CalendarSource,
    rowId: string
  ): Promise<CalendarLocalEventCreateResult> {
    const opened = await this.discovery.openDocWithContent(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Selected calendar view is not available locally.');
      }
      if (!source.canEdit || localView.view.readonly$.value) {
        throw new Error('Selected calendar is read-only.');
      }
      const previousEvent = readCalendarEvent(localView.view, rowId, {
        includeDetails: true,
      });
      if (!previousEvent) {
        throw new Error('Calendar event is not available locally.');
      }
      const context = resolveCalendarReconcileContext(
        localView.view.manager.dataSource
      );
      const columns = calendarReconcileColumns(localView.view);
      if (!context || !columns) {
        throw new Error('Calendar reconcile context is unavailable.');
      }

      localView.view.rowsDelete([rowId]);
      return {
        reconcile: {
          columns,
          context,
          previousEvent: toCalendarReconcileSnapshot(
            previousEvent,
            context,
            localView.view.id
          ),
          rowId,
          skipMail: true,
          viewId: localView.view.id,
        },
        rowId,
        startAt: previousEvent.startAt.toISOString(),
      };
    } finally {
      opened.release();
    }
  }

  async observeEventChanges(
    sources: readonly CalendarSource[],
    onChange: () => void
  ): Promise<() => void> {
    const releases: Array<() => void> = [];
    const observedDocs = new Set<object>();

    const observeDoc = (doc: CalendarObservedStore) =>
      observeCalendarDocument(doc, onChange, observedDocs, releases);

    for (const source of sources) {
      const opened = await this.discovery
        .openDocWithContent(source.docId)
        .catch(() => null);
      if (!opened) continue;
      releases.push(opened.release);
      observeDoc(opened.store);

      const view = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      ).catch(() => null);
      if (!view?.dataSource?.storageModel) continue;
      observeCalendarRowDocs(view.dataSource, observeDoc, onChange, releases);
    }

    return () => {
      for (const release of releases.reverse()) release();
    };
  }

  async renameLocalSource(
    source: CalendarSource,
    title: string
  ): Promise<void> {
    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
      throw new Error('Calendar title must not be empty.');
    }
    const opened = await this.discovery.openDocWithContent(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.view) {
        throw new Error('Calendar view is not available locally.');
      }
      if (localView.view.readonly$.value) {
        throw new Error('Calendar view is read-only.');
      }
      localView.view.nameSet(normalizedTitle);
    } finally {
      opened.release();
    }
  }

  /** Make the calendar view the one its database shows on the canvas. */
  async presentLocalSourceOnCanvas(source: CalendarSource): Promise<void> {
    const opened = await this.discovery.openDocWithContent(source.docId);
    try {
      const localView = await this.findCalendarView(
        opened.store,
        source.databaseId,
        source.viewId
      );
      if (!localView?.dataSource) {
        throw new Error('Calendar database is not available locally.');
      }
      // Read-only viewers keep the database's own presentation.
      if (localView.view?.readonly$.value !== false) {
        return;
      }
      presentCanvasDatabaseView(localView.dataSource, source.viewId);
    } finally {
      opened.release();
    }
  }

  private async findCalendarView(
    store: Parameters<
      DatabaseViewLocalDiscoveryService['findLocalView']
    >[0]['store'],
    databaseId: string,
    viewId: string
  ): Promise<LocalCalendarView | null> {
    const view = await this.discovery.findLocalView({
      databaseId,
      descriptor: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      store,
      viewId,
    });
    return view ? this.toCalendarView(view) : null;
  }

  private toCalendarView(view: LocalDatabaseView): LocalCalendarView {
    if (view.view && (view.view as { type?: unknown }).type === 'calendar') {
      return {
        ...view,
        columns: (view.diagnostics ?? {}) as CalendarEventColumnMap,
        view: view.view as CalendarSingleView,
      };
    }
    return {
      ...view,
      columns: {},
      projectionError: view.projectionError ?? 'projection-failed',
      view: null,
    };
  }

  private toLocalSource(input: {
    account: CalendarLocalAccount | null;
    docId: string;
    projectionError: string | null;
    view: LocalCalendarView;
  }): CalendarSource {
    const id = viewBackedAppResourceId(
      CALENDAR_VIEW_BACKED_APP_DESCRIPTOR,
      input.docId,
      input.view.databaseId,
      input.view.viewId
    );
    const canEdit = input.view.view?.readonly$.value === false;
    const area = input.view.area ? toCalendarAreaRef(input.view.area) : null;
    const presentation = resolveLocalCalendarPresentation({
      account: input.account,
      area,
    });
    return {
      accessDocId: input.docId,
      appKind: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.appKind,
      area,
      blockId: input.view.blockId,
      canEdit,
      canManage: canEdit,
      color: localCalendarSourceColor(id),
      columns: input.view.columns,
      databaseId: input.view.databaseId,
      deletedAt: null,
      docId: input.docId,
      id,
      lifecycleState: 'active',
      owner: presentation.owner,
      presentationError: presentation.presentationError,
      projectionError: input.projectionError ?? input.view.projectionError,
      scope: viewBackedAreaScope(area),
      sourcePresentationContractVersion: 1,
      syncState: 'local',
      title: input.view.title,
      trashedAt: null,
      viewId: input.view.viewId,
      viewMode: CALENDAR_VIEW_BACKED_APP_DESCRIPTOR.viewMode,
      visible: true,
    };
  }

  private projectSourceEvents(input: {
    end: Date;
    source: CalendarSource;
    start: Date;
    view: CalendarSingleView;
  }): CalendarProjectedEvent[] | undefined {
    const startDateCol = input.view.startDateCol$.value;
    if (!startDateCol) return [];
    type ProjectionQuery = Parameters<CalendarSingleView['projectRows']>[0];
    const predicates: NonNullable<ProjectionQuery['predicates']> = [
      {
        endPropertyId: input.view.endDateCol$.value,
        startPropertyId: startDateCol,
        type: 'temporal-overlap',
        windowEnd: input.end.getTime(),
        windowStart: input.start.getTime(),
      },
    ];
    const projection = input.view.projectRows({
      predicates,
      scope: 'temporal-window',
      viewId: input.view.id,
      viewMode: input.view.type,
    });
    // An incomplete local window is not authoritative evidence of absence.
    // Keep the server rows and revalidate rather than publishing an empty,
    // successful local projection that suppresses them during hydration.
    if (!projection.complete) {
      if (projection.syncState === 'syncing') return undefined;
      throw new CalendarCommandError(
        'unavailable',
        'calendar.chrome.unavailable'
      );
    }
    return projection.rows
      .flatMap(row => {
        try {
          const event = readCalendarEvent(input.view, row.rowId, {
            includeDetails: true,
          });
          return event ? [event] : [];
        } catch (error) {
          console.warn(
            '[CalendarLocalProjection] Failed to read calendar event row',
            {
              error,
              rowId: row.rowId,
              sourceId: input.source.id,
            }
          );
          return [];
        }
      })
      .map(event => {
        const columns = input.source.columns;
        return {
          allDay: event.allDay,
          attendeeCount: parseCalendarAttendees(event.attendees).length,
          canEdit: input.source.canEdit,
          color: input.source.color,
          endAt: event.endAt.toISOString(),
          icsSequence: readOptionalCalendarNumber(
            input.view,
            event.rowId,
            columns?.icsSequenceCol
          ),
          icsUid: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.icsUidCol
          ),
          id: `${input.source.id}:${event.rowId}`,
          invitationMethod: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.invitationMethodCol
          ),
          invitationState: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.invitationStateCol
          ),
          lifecycleState: input.source.lifecycleState,
          physicalLocation: event.locationPhysical || null,
          rowId: event.rowId,
          sourceId: input.source.id,
          sourceMailAccountId: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailAccountIdCol
          ),
          sourceMailAttachmentBlobKey: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailAttachmentBlobKeyCol
          ),
          sourceMailMessageId: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceMailMessageIdCol
          ),
          sourceType: readOptionalCalendarString(
            input.view,
            event.rowId,
            columns?.sourceTypeCol
          ),
          startAt: event.startAt.toISOString(),
          status: event.status || null,
          timezone:
            event.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone ||
            'UTC',
          title: event.title,
          virtualLocation: event.locationVirtual || null,
        };
      });
  }
}

function readOptionalCalendarString(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): string | null {
  if (!propertyId) return null;
  const value = view.cellGetOrCreate(rowId, propertyId).stringValue$.value;
  return value || null;
}

function readOptionalCalendarNumber(
  view: CalendarSingleView,
  rowId: string,
  propertyId: string | undefined
): number | null {
  if (!propertyId) return null;
  const value = view.cellGetOrCreate(rowId, propertyId).jsonValue$.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function projectedSource(source: CalendarSource, error: string | null) {
  return {
    canEdit: source.canEdit,
    canManage: source.canManage,
    color: source.color,
    error,
    id: source.id,
    lifecycleState: source.lifecycleState,
    title: source.title,
    visible: source.visible,
  };
}

function parseWindowDate(value: string, field: string): Date {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    throw new Error(`Invalid calendar ${field}: ${value}`);
  }
  return date;
}

function compareSources(a: CalendarSource, b: CalendarSource): number {
  return a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}

/**
 * Color of a local-only calendar, derived from its source id so it stays the
 * same whether one calendar or the whole workspace is projected.
 */
export function localCalendarSourceColor(sourceId: string): string {
  let hash = 0;
  for (let index = 0; index < sourceId.length; index++) {
    hash = (hash * 31 + sourceId.charCodeAt(index)) >>> 0;
  }
  return CALENDAR_SOURCE_COLORS[hash % CALENDAR_SOURCE_COLORS.length];
}

type CalendarObservedStore = {
  spaceDoc?: {
    on: (event: 'update', callback: () => void) => void;
    off: (event: 'update', callback: () => void) => void;
  };
};
/**
 * Observes the documents holding the rows of a calendar database: its root,
 * its directory parts and the row shards they list. Rows of parts that arrive
 * later report through the data source, which requests them here.
 */
function observeCalendarRowDocs(
  dataSource: NonNullable<LocalCalendarView['dataSource']>,
  observe: (doc: CalendarObservedStore) => void,
  onChange: () => void,
  stops: Array<() => void>
): void {
  const storage = dataSource.storageModel;
  observe(storage.store);
  dataSource.getCompleteShardDirectory();
  const docIds = readDatabaseShardDirectoryDocIds(storage);
  for (const docId of [
    ...(docIds?.partDocIds ?? []),
    ...(docIds?.shardDocIds ?? []),
  ]) {
    const doc = storage.store.workspace.getDoc(docId);
    if (doc) observe(doc);
  }
  stops.push(dataSource.onShardedRowsChange(onChange));
}

function observeCalendarDocument(
  store: CalendarObservedStore,
  onChange: () => void,
  observed: Set<object>,
  stops: Array<() => void>
): void {
  const doc = store.spaceDoc;
  if (!doc || observed.has(doc)) return;
  observed.add(doc);
  doc.on('update', onChange);
  stops.push(() => doc.off('update', onChange));
}
